import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subscription, take } from 'rxjs';
import { debounce } from 'lodash';
import { DateTime } from 'luxon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PaginationComponent } from 'shared/components/pagination/pagination.component';
import { AdminRestApiService } from 'shared/services/api/admin-rest-api.api.service';

type Tab = 'accounts' | 'webhooks' | 'logs' | 'offenders';

@Component({
  standalone: true,
  selector: 'app-admin-rest-api',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PaginationComponent,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: './admin-rest-api.component.html',
})
export class AdminRestApiComponent implements OnDestroy {

  tab: Tab = 'accounts';
  private _subs: Subscription[] = [];

  // --- Konta API ---
  accounts: any[] = [];
  accountsLoading = false;
  accountsSearch = new FormControl('');
  accountsPg = { currentPage: 1, itemsPerPage: 20, count: 0, pages: 0 };

  // --- Webhooki ---
  webhooks: any[] = [];
  webhooksLoading = false;
  webhooksSearch = new FormControl('');
  webhooksPg = { currentPage: 1, itemsPerPage: 20, count: 0, pages: 0 };

  // --- Logi ---
  logs: any[] = [];
  logsLoading = false;
  logsSearch = new FormControl('');
  logsAccountEmail = new FormControl('');
  logsMethod = '';
  logsStatusClass = '';
  logsType = '';
  logsPg = { currentPage: 1, itemsPerPage: 20, count: 0, pages: 0 };
  selectedLog: any = null;

  // --- Naruszenia ---
  offendersLoading = false;
  offendersAvailable = true;
  offenders: { identifier: string; cooldowns: number }[] = [];
  activeCooldowns: { key: string; cooldownSeconds: number }[] = [];

  // --- Statystyki magazynu logów ---
  stats: any = null;

  constructor(private api: AdminRestApiService) {
    this.browseAccounts = debounce(this.browseAccounts, 300);
    this.browseWebhooks = debounce(this.browseWebhooks, 300);
    this.browseLogs = debounce(this.browseLogs, 300);

    this._subs.push(this.accountsSearch.valueChanges.subscribe(() => { this.accountsPg.currentPage = 1; this.browseAccounts(); }));
    this._subs.push(this.webhooksSearch.valueChanges.subscribe(() => { this.webhooksPg.currentPage = 1; this.browseWebhooks(); }));
    this._subs.push(this.logsSearch.valueChanges.subscribe(() => { this.logsPg.currentPage = 1; this.browseLogs(); }));
    this._subs.push(this.logsAccountEmail.valueChanges.subscribe(() => { this.logsPg.currentPage = 1; this.browseLogs(); }));

    this.browseAccounts();
    this.loadStats();
  }

  ngOnDestroy(): void {
    this._subs.forEach(s => s.unsubscribe());
  }

  setTab(tab: Tab): void {
    this.tab = tab;
    if (tab === 'webhooks' && !this.webhooks.length) this.browseWebhooks();
    if (tab === 'logs' && !this.logs.length) this.browseLogs();
    if (tab === 'offenders') this.loadOffenders();
  }

  // ---------------------------------------------------------------------------
  // Konta API
  // ---------------------------------------------------------------------------
  browseAccounts(): void {
    this.accountsLoading = true;
    this.api.browseAccounts({
      searchQuery: this.accountsSearch.value || '',
      currentPage: this.accountsPg.currentPage,
      itemsPerPage: this.accountsPg.itemsPerPage,
    }).pipe(take(1)).subscribe({
      next: (res: any) => {
        if (res?.success) {
          this.accounts = res.data.accounts || [];
          this.accountsPg.count = res.data.count;
          this.accountsPg.pages = res.data.pages;
        }
        this.accountsLoading = false;
      },
      error: () => { this.accountsLoading = false; },
    });
  }

  onAccountsPage(page: number): void { this.accountsPg.currentPage = page; this.browseAccounts(); }

  // ---------------------------------------------------------------------------
  // Webhooki
  // ---------------------------------------------------------------------------
  browseWebhooks(): void {
    this.webhooksLoading = true;
    this.api.browseWebhooks({
      searchQuery: this.webhooksSearch.value || '',
      currentPage: this.webhooksPg.currentPage,
      itemsPerPage: this.webhooksPg.itemsPerPage,
    }).pipe(take(1)).subscribe({
      next: (res: any) => {
        if (res?.success) {
          this.webhooks = res.data.webhooks || [];
          this.webhooksPg.count = res.data.count;
          this.webhooksPg.pages = res.data.pages;
        }
        this.webhooksLoading = false;
      },
      error: () => { this.webhooksLoading = false; },
    });
  }

  onWebhooksPage(page: number): void { this.webhooksPg.currentPage = page; this.browseWebhooks(); }

  // ---------------------------------------------------------------------------
  // Logi
  // ---------------------------------------------------------------------------
  browseLogs(): void {
    this.logsLoading = true;
    this.api.browseLogs({
      searchQuery: this.logsSearch.value || '',
      method: this.logsMethod || undefined,
      statusClass: this.logsStatusClass || undefined,
      type: this.logsType || undefined,
      accountEmail: this.logsAccountEmail.value || undefined,
      currentPage: this.logsPg.currentPage,
      itemsPerPage: this.logsPg.itemsPerPage,
    }).pipe(take(1)).subscribe({
      next: (res: any) => {
        if (res?.success) {
          this.logs = (res.data.logs || []).map((l: any) => this.normLog(l));
          this.logsPg.count = res.data.count;
          this.logsPg.pages = res.data.pages;
        }
        this.logsLoading = false;
      },
      error: () => { this.logsLoading = false; },
    });
  }

  onLogsPage(page: number): void { this.logsPg.currentPage = page; this.browseLogs(); }

  selectLog(l: any): void {
    // drugi klik w ten sam wiersz chowa panel; wybór innego przełącza i doscrolluje
    this.selectedLog = this.selectedLog === l ? null : l;
    if (this.selectedLog) {
      setTimeout(() => document.getElementById('log-detail')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 0);
    }
  }

  setLogsStatusClass(cls: string): void {
    this.logsStatusClass = this.logsStatusClass === cls ? '' : cls;
    this.logsPg.currentPage = 1;
    this.browseLogs();
  }

  setLogsMethod(method: string): void {
    this.logsMethod = this.logsMethod === method ? '' : method;
    this.logsPg.currentPage = 1;
    this.browseLogs();
  }

  setLogsType(type: string): void {
    this.logsType = this.logsType === type ? '' : type;
    this.logsPg.currentPage = 1;
    this.browseLogs();
  }

  /** Ujednolica wpis logu niezależnie od sinka (SQLite snake_case / Mongo camelCase). */
  private normLog(l: any): any {
    const ts = l.ts ?? (l.createdAt ? new Date(l.createdAt).getTime() : null);
    const account = l.api_account_id ?? l.apiAccountId ?? null;
    return {
      ts,
      ruid: l.ruid ?? null,
      type: l.type ?? null,
      method: l.method,
      endpoint: l.endpoint,
      status: l.status_code ?? l.statusCode,
      execMs: l.exec_ms ?? l.executionTimeMs,
      account: account && typeof account === 'object' ? (account.name || account.clientId || account._id) : account,
      ip: l.ip ?? l.ipAddress ?? null,
      reqBody: l.req_body ?? this.stringify(l.requestBody),
      resBody: l.res_body ?? this.stringify(l.responseBody),
      reqTruncated: !!l.req_truncated,
      resTruncated: !!l.res_truncated,
    };
  }

  private stringify(v: any): string | null {
    if (v === undefined || v === null) return null;
    if (typeof v === 'string') return v;
    try { return JSON.stringify(v); } catch { return String(v); }
  }

  // ---------------------------------------------------------------------------
  // Naruszenia limitów (offenders)
  // ---------------------------------------------------------------------------
  loadOffenders(): void {
    this.offendersLoading = true;
    this.api.getOffenders().pipe(take(1)).subscribe({
      next: (res: any) => {
        if (res?.success) {
          this.offendersAvailable = res.data.available !== false;
          this.offenders = res.data.offenders || [];
          this.activeCooldowns = res.data.active || [];
        }
        this.offendersLoading = false;
      },
      error: () => { this.offendersLoading = false; },
    });
  }

  loadStats(): void {
    this.api.getStats().pipe(take(1)).subscribe({
      next: (res: any) => { if (res?.success) this.stats = res.data; },
    });
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------
  rowNumber(page: number, perPage: number, index: number): number {
    return (page - 1) * perPage + index + 1;
  }

  fmtDate(value: any): string {
    if (!value) return '-';
    const dt = typeof value === 'number' ? DateTime.fromMillis(value) : DateTime.fromISO(String(value));
    return dt.isValid ? dt.toFormat('dd.MM.yyyy HH:mm:ss') : '-';
  }

  statusColor(status: number): string {
    if (status >= 500) return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    if (status === 429) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
    if (status >= 400) return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
    if (status >= 200 && status < 300) return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  }

  webhookStatusColor(status: string): string {
    if (status === 'ACTIVE') return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    if (status === 'ERROR') return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  }

  ownerLabel(userId: any): string {
    if (!userId || typeof userId !== 'object') return '-';
    const name = [userId.firstName, userId.lastName].filter(Boolean).join(' ');
    return userId.emailAddress || name || userId.company?.name || '-';
  }
}
